import type { ReactNode } from 'react';
import { AlertTriangle, FileDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { DisabledHintTooltip } from '@ooh/ui/components/disabled-hint-tooltip';
import { InlineErrorMessage } from '@ooh/ui/components/inline-error-message';
import { LockedNote } from '@ooh/ui/components/locked-note';

export function OrderDocumentAction({
  isDraft,
  isDownloading,
  onDownloadOrderDocument,
}: {
  isDraft: boolean;
  isDownloading: boolean;
  onDownloadOrderDocument: () => void;
}) {
  const { t } = useTranslation('offers');
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        disabled={isDownloading}
        onClick={onDownloadOrderDocument}
      >
        <FileDown className="h-3.5 w-3.5" />
        {isDraft
          ? t('workspace.header.finalizationPanel.orderDocument.draftLabel')
          : t('workspace.header.actions.downloadOrderDocument')}
      </Button>
      {isDraft ? (
        <span className="text-xs text-muted-foreground">
          {t('workspace.header.finalizationPanel.orderDocument.draftHint')}
        </span>
      ) : null}
    </div>
  );
}

function UnsecuredAlert({ onSecure }: { onSecure: () => void }) {
  const { t } = useTranslation('offers');
  return (
    <InlineErrorMessage
      tone="warning"
      icon={AlertTriangle}
      action={
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={onSecure}
          data-testid="secure-remaining-btn"
        >
          {t('workspace.header.actions.secureRemainingUnits')}
        </Button>
      }
    >
      {t('workspace.header.finalizationPanel.confirmed.unsecuredAlert')}
    </InlineErrorMessage>
  );
}

/**
 * Single fail-closed gate for every access-controlled finalization action:
 * render the action when permitted, render nothing while access is still
 * resolving (unresolved access reads as "no" but must never assert a denial
 * — offer-approval / "Approval Action UI in Offer Workspace"), otherwise
 * render the blocked note. Centralized so this rule is fixed once, not
 * re-applied per step.
 */
function GatedAction({
  isPermitted,
  isAccessResolved,
  action,
  blockedNote,
}: {
  isPermitted: boolean;
  isAccessResolved: boolean;
  action: ReactNode;
  blockedNote: ReactNode;
}) {
  if (isPermitted) return action;
  if (!isAccessResolved) return null;
  return blockedNote;
}

/**
 * The preliminary-reservation action is hidden entirely for a caller with no
 * offer edit access at all — unlike every other step's gate, it grants no
 * approve_any-style bypass, so `GatedAction`'s "blocked, not missing" pattern
 * only applies once edit access is confirmed present (offer-accept-
 * reservation / "The action does not render for a caller with no edit
 * access at all").
 */
export function ReservePreliminaryAction({
  canEditOffer,
  canManageReservation,
  isAccessResolved,
  hasWork,
  onReserve,
}: {
  canEditOffer: boolean;
  canManageReservation: boolean;
  isAccessResolved: boolean;
  hasWork: boolean;
  onReserve: () => void;
}) {
  const { t } = useTranslation('offers');
  if (!canEditOffer) return null;

  return (
    <GatedAction
      isPermitted={canManageReservation}
      isAccessResolved={isAccessResolved}
      action={
        <DisabledHintTooltip
          disabled={!hasWork}
          hint={t('workspace.header.finalizationPanel.accepted.reserveDisabledHint')}
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasWork}
            onClick={onReserve}
            data-testid="reserve-preliminary-btn"
          >
            {t('workspace.header.actions.reservePreliminary')}
          </Button>
        </DisabledHintTooltip>
      }
      blockedNote={
        <LockedNote>
          {t('workspace.header.finalizationPanel.accepted.reserveBlockedNote')}
        </LockedNote>
      }
    />
  );
}

export function AcceptedConfirmAction({
  canConfirmCampaign,
  isAccessResolved,
  onConfirmCampaign,
}: {
  canConfirmCampaign: boolean;
  isAccessResolved: boolean;
  onConfirmCampaign: () => void;
}) {
  const { t } = useTranslation('offers');
  return (
    <GatedAction
      isPermitted={canConfirmCampaign}
      isAccessResolved={isAccessResolved}
      action={
        <Button
          type="button"
          variant="success"
          size="sm"
          onClick={onConfirmCampaign}
          data-testid="confirm-campaign-btn"
        >
          {t('workspace.header.actions.confirmCampaign')}
        </Button>
      }
      blockedNote={
        <LockedNote>{t('workspace.header.finalizationPanel.accepted.blockedNote')}</LockedNote>
      }
    />
  );
}

export function ConfirmedRecordSaleAction({
  canRecordSale,
  isAccessResolved,
  onRecordSale,
}: {
  canRecordSale: boolean;
  isAccessResolved: boolean;
  onRecordSale: () => void;
}) {
  const { t } = useTranslation('offers');
  return (
    <GatedAction
      isPermitted={canRecordSale}
      isAccessResolved={isAccessResolved}
      action={
        <Button
          type="button"
          variant="success"
          size="sm"
          onClick={onRecordSale}
          data-testid="record-sale-btn"
        >
          {t('workspace.header.actions.recordSale')}
        </Button>
      }
      blockedNote={
        <LockedNote>{t('workspace.header.finalizationPanel.confirmed.blockedNote')}</LockedNote>
      }
    />
  );
}

/**
 * Securing the remainder is the same access-gated action as confirming the
 * campaign (offer-approval / "Approval Action UI in Offer Workspace"), so
 * this reuses the confirm-campaign gate rather than introducing a second one.
 */
export function ConfirmedUnsecuredSection({
  hasUnsecuredUnits,
  canConfirmCampaign,
  isAccessResolved,
  onSecureRemaining,
}: {
  hasUnsecuredUnits: boolean;
  canConfirmCampaign: boolean;
  isAccessResolved: boolean;
  onSecureRemaining: () => void;
}) {
  const { t } = useTranslation('offers');
  if (!hasUnsecuredUnits) return null;
  return (
    <GatedAction
      isPermitted={canConfirmCampaign}
      isAccessResolved={isAccessResolved}
      action={<UnsecuredAlert onSecure={onSecureRemaining} />}
      blockedNote={
        <LockedNote>
          {t('workspace.header.finalizationPanel.confirmed.secureBlockedNote')}
        </LockedNote>
      }
    />
  );
}
